import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  PlusOutlined as AddIcon,
  UnorderedListOutlined as DynamicFeedOutlined,
  EditOutlined,
  SaveOutlined as Save,
  SettingOutlined as SettingsIcon
} from '@ant-design/icons';
import { Alert, Avatar, Badge, Breadcrumb, Button, Spin, Tabs, Tooltip } from 'antd';

import { useQueryClient } from '@tanstack/react-query';
import CoProducerClass from 'classes/CoProducer';
import ProductClass from 'classes/Product';
import AlertComponent from 'components/Alert';
import { TOOLBAR_HEIGHT } from 'components/Layout';
import ModalActions from 'components/ModalActions';
import { constants } from 'helpers/constants';
import { EProductStatus } from 'helpers/enums';
import { functions } from 'helpers/functions';
import type ICoProducer from 'interfaces/ICoProducer';
import { EChargeType } from 'interfaces/IEditProduct';
import type IProduct from 'interfaces/IProduct';
import InfoDelivery from 'scenes/Producer/Product/components/InfoDeliveryMethods';
import InfoDeliveryValidations from 'scenes/Producer/Product/components/InfoDeliveryMethods/validations';
import Alumy from 'scenes/Producer/Product/components/Partners/Alumy';
import PhysicalDelivery from 'scenes/Producer/Product/components/PhysicalDeliveryMethods';
import PDCorreiosValidations from 'scenes/Producer/Product/components/PhysicalDeliveryMethods/Form/Correios/validations';
import PDFixedValueValidations from 'scenes/Producer/Product/components/PhysicalDeliveryMethods/Form/FixedValue/validations';
import PDFreeValidations from 'scenes/Producer/Product/components/PhysicalDeliveryMethods/Form/Free/validations';
import { BLINKET_URL, VERTICAL_URL } from 'settings';
import Truncate from 'v2/components/Shared/TruncateText';
import { isLegalPersonAccount } from 'v2/helpers/isLegalPersonAccount';
import { getProductTypeTitle } from 'v2/helpers/productTypeTitle';
import { useCategories } from 'v2/hooks/products/useCategories';
import { useProduct } from 'v2/hooks/products/useProduct';
import { useNutror } from 'v2/hooks/useNutror';
import { usePostMessage } from 'v2/hooks/usePostMessage';
import { PostMessageAction, PostMessageSource } from 'v2/interfaces/post-message';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import productService from 'v2/services/product';
import { usePhysicalDeliveryStore } from 'v2/store/PhysicalDelivery';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import DraftButtons from '../components/DraftButtons';
import DraftLabel from '../components/DraftLabel';
import AdvancedForm from './components/Advanced';
import AdvancedValidations from './components/Advanced/validations';
import Affiliate from './components/Affiliate';
import AffiliateValidations from './components/Affiliate/validations';
import CoProducer from './components/CoProducer';
import CoProducerFormValidations from './components/CoProducer/components/CoProducerForm/validations';
import CoProducerValidations from './components/CoProducer/validations';
import General from './components/General';
import MarketplaceValidation from './components/General/marketplaceValidation';
import GeneralValidations from './components/General/validations';
import PaymentMethods from './components/PaymentMethods';
import PaymentMethodValidations from './components/PaymentMethods/validations';
import Sales from './components/Sales';
import { allUsersSubscriptionValidation } from './components/Sales/components/Subscription/validations';
import SalesRecovery from './components/SalesRecovery/index';
import RecoveryValidations from './components/SalesRecovery/validations';

enum eNameTabs {
  General = 'general',
  Sales = 'sales',
  SubscriptionPlan = 'subscriptionplan',
  PaymentMethods = 'paymentmethods',
  Coproducers = 'coproducers',
  Affiliates = 'affiliates',
  Delivery = 'delivery',
  InfoDelivery = 'infodelivery',
  PhysicalDelivery = 'physicaldelivery',
  SalesRecovery = 'salesrecovery',
  Advanced = 'advanced'
}

const coProducersLimitWithElevar = 16;
const coProducersLimitWithoutElevar = 15;

const CORREIOS = '3';
const FIXEDVALUE = '2';
const FREE = '1';

const PHYSICAL_DELIVERY_VALIDATORS: Record<string, any> = {
  [FIXEDVALUE]: PDFixedValueValidations,
  [FREE]: PDFreeValidations,
  [CORREIOS]: PDCorreiosValidations
};

function countErrors(data: any, validations: any): number {
  const errors = run(data, validations);
  return errors ? Object.keys(errors).length : 0;
}

function stripEdzPrefix(name: string): string {
  return name.startsWith('EDZ ') ? name.slice(4) : name;
}

interface IRouteParams {
  tab?: any;
  productId: any;
  variationId?: any;
  tabName?: any;
}

interface IStates {
  selectedProduct: IProduct;
  product: IProduct;
  variations: Array<IProduct>;
  isVariation: boolean;
  originalProduct: IProduct;
  modifieds: any;
  tabSelected: string;
  saving: boolean;
  loading: boolean;
  loadingGeneral: boolean;
  loadingCoProducers: boolean;
  loadingDeliveryMethods: boolean;
  generalRequiredFields: number;
  salesRequiredFields: number;
  coProducerRequiredFields: number;
  InfoDeliveryRequiredFields: any;
  PhysicalDeliveryRequiredFields: any;
  salesSinglePaymentRequiredFields: number;
  salesSubscriptionRequiredFields: number;
  advancedRequiredFields: number;
  affiliatesRequiredFields: number;
  paymentMethodsRequiredFields: number;
  recoveryRequiredFields: number;
  selected: any;
  productId: any;
  variationId: any;
  editing: boolean;
  confirmModal: boolean;
  nextItem: any;
  variationModal: boolean;
  blinketModal: boolean;
  prevSelected?: IProduct;
  alert: boolean;
  oldEdit: boolean;
  cancelModalCallback: undefined | (() => void);
}

const PROJECT_ELEVAR_ID = [75879698];

const initialState: IStates = {
  originalProduct: new ProductClass(),
  isVariation: false,
  variations: null,
  product: new ProductClass(),
  selectedProduct: new ProductClass(),
  modifieds: {},
  tabSelected: eNameTabs.General,
  saving: false,
  loading: true,
  loadingGeneral: true,
  loadingCoProducers: true,
  loadingDeliveryMethods: true,
  generalRequiredFields: 0,
  salesRequiredFields: 0,
  advancedRequiredFields: 0,
  coProducerRequiredFields: 0,
  InfoDeliveryRequiredFields: 0,
  PhysicalDeliveryRequiredFields: 0,
  salesSinglePaymentRequiredFields: 0,
  salesSubscriptionRequiredFields: 0,
  affiliatesRequiredFields: 0,
  paymentMethodsRequiredFields: 0,
  recoveryRequiredFields: 0,
  selected: null,
  productId: null,
  variationId: null,
  editing: false,
  confirmModal: false,
  nextItem: null,
  variationModal: false,
  alert: localStorage.getItem('news_myproducts_unlimited_installment') === 'true',
  oldEdit: false,
  cancelModalCallback: undefined,
  blinketModal: false
};

function stateReducer(prev: IStates, next: Partial<IStates>): IStates {
  return { ...prev, ...next };
}

const Product = () => {
  const history = useHistory();
  const routeParams = useParams<IRouteParams>();
  const queryClient = useQueryClient();
  const routeProductId = Number(routeParams.productId);
  const { product: fetchedProduct } = useProduct(routeProductId);
  const { categories } = useCategories();
  const sendPostMessage = usePostMessage();

  const { user, originalUser } = useUserStore(({ user, originalUser }) => ({ user, originalUser }));
  const isLegalPerson = isLegalPersonAccount(originalUser?.type);
  const {
    delivery: physicalDelivery,
    setDelivery: setPhysicalDelivery,
    reset: resetPhysicalDelivery
  } = usePhysicalDeliveryStore();

  const [state, updateState] = useReducer(stateReducer, initialState);
  const { courses, packages } = useNutror();

  const stateRef = useRef(state);
  stateRef.current = state;

  const physicalDeliveryRef = useRef(physicalDelivery);
  physicalDeliveryRef.current = physicalDelivery;

  const isFirstRender = useRef(true);

  function verifyValidations(product: IProduct): void {
    let generalRequiredFields = countErrors(product, GeneralValidations);

    if (product.hasVariations() && !product.parent_id) {
      generalRequiredFields += countErrors(product, MarketplaceValidation);
    }

    const affiliatesRequiredFields = product?.allow_affiliation ? countErrors(product, AffiliateValidations) : 0;

    if (!product || product.isMainWithChildren()) {
      updateState({
        generalRequiredFields,
        salesRequiredFields: 0,
        InfoDeliveryRequiredFields: 0,
        advancedRequiredFields: 0,
        coProducerRequiredFields: 0,
        PhysicalDeliveryRequiredFields: 0,
        affiliatesRequiredFields,
        paymentMethodsRequiredFields: 0,
        recoveryRequiredFields: 0
      });
      return;
    }

    const advancedRequiredFields = countErrors(product, AdvancedValidations);
    const paymentMethodsRequiredFields = countErrors(product, PaymentMethodValidations);
    const recoveryRequiredFields = countErrors(product, RecoveryValidations);

    const salesRequiredFields = product.charge_type === 'A' ? countErrors(product, allUsersSubscriptionValidation) : 0;

    const coProducerRequiredFields =
      countErrors(product, CoProducerValidations) +
      (product.coproducers ?? []).reduce(
        (sum: number, cp: any) => sum + countErrors(cp, CoProducerFormValidations()),
        0
      );

    const InfoDeliveryRequiredFields =
      product.type == constants.INFO ? countErrors(product, InfoDeliveryValidations) : 0;

    const deliveryValidator =
      product.type == constants.PHYSICAL
        ? PHYSICAL_DELIVERY_VALIDATORS[String(product.physicalDelivery?.delivery_type ?? '')]
        : null;
    const PhysicalDeliveryRequiredFields = deliveryValidator
      ? countErrors(product.physicalDelivery, deliveryValidator)
      : 0;

    updateState({
      generalRequiredFields,
      salesRequiredFields,
      InfoDeliveryRequiredFields,
      advancedRequiredFields,
      coProducerRequiredFields,
      PhysicalDeliveryRequiredFields,
      affiliatesRequiredFields,
      paymentMethodsRequiredFields,
      recoveryRequiredFields
    });
  }

  function formHasError(): boolean {
    const {
      generalRequiredFields,
      advancedRequiredFields,
      salesRequiredFields,
      coProducerRequiredFields,
      InfoDeliveryRequiredFields,
      PhysicalDeliveryRequiredFields,
      affiliatesRequiredFields,
      paymentMethodsRequiredFields,
      recoveryRequiredFields,
      selectedProduct
    } = stateRef.current;

    if (selectedProduct.isMainWithChildren() && generalRequiredFields === 0) return false;

    if (selectedProduct.isMainWithChildren()) {
      toast.error('Existem campos inválidos no produto, por favor verifique-os antes de salvar');
      updateState({ tabSelected: eNameTabs.General });
      return true;
    }

    const tabErrorMap = [
      { count: generalRequiredFields, tab: eNameTabs.General },
      { count: salesRequiredFields, tab: eNameTabs.Sales },
      { count: coProducerRequiredFields, tab: eNameTabs.Coproducers },
      { count: affiliatesRequiredFields, tab: eNameTabs.Affiliates },
      { count: InfoDeliveryRequiredFields, tab: eNameTabs.InfoDelivery },
      { count: advancedRequiredFields, tab: eNameTabs.Advanced },
      { count: paymentMethodsRequiredFields, tab: eNameTabs.PaymentMethods },
      { count: PhysicalDeliveryRequiredFields, tab: eNameTabs.PhysicalDelivery },
      { count: recoveryRequiredFields, tab: eNameTabs.SalesRecovery }
    ];

    const totalErrors = tabErrorMap.reduce((sum, e) => sum + e.count, 0);
    if (totalErrors === 0) return false;

    const hasPhysicalDeliveryErrors = selectedProduct.type === constants.PHYSICAL && PhysicalDeliveryRequiredFields > 0;
    const tabSelected = hasPhysicalDeliveryErrors
      ? eNameTabs.PhysicalDelivery
      : (tabErrorMap.find(e => e.count > 0)?.tab ?? eNameTabs.General);
    toast.error('Existem campos inválidos no produto, por favor verifique-os antes de salvar');
    updateState({ tabSelected });
    return true;
  }

  function convertToNumber(value: string): number {
    return Number(value.toString().replace(',', '.'));
  }

  function handleDelivery(selectedProduct: IProduct, deliveriesWithoutNutrorHash: any[]) {
    const adjustDeliveriesWithoutNutrorHash = deliveriesWithoutNutrorHash.map(deliveryItem => {
      const c = courses ?? [];
      const p = packages ?? [];
      const course = [...c, ...p].find(co => co.id == deliveryItem?.nutror_id);

      if (course) {
        deliveryItem.nutror_hash = course.hash;
      }

      return deliveryItem;
    });
    return [...selectedProduct.getDeliveries().filter(d => !!d.nutror_hash), ...adjustDeliveriesWithoutNutrorHash];
  }

  function getDefaultTab(product: IProduct): string {
    const { variationId, tabName } = routeParams;
    const isPlansBeta = eduzzAccountsService.verifyUserHasBetaModule('planos_assinatura');
    const isSubscription = product.charge_type === EChargeType.SUBSCRIPTION;
    const isVariationParam = !Object.values(eNameTabs).includes(variationId);
    if (isPlansBeta && isSubscription && (variationId === eNameTabs.Sales || tabName === eNameTabs.Sales))
      return eNameTabs.SubscriptionPlan;
    if (isVariationParam) return tabName || eNameTabs.General;
    return variationId || eNameTabs.General;
  }

  function handleMarketplaceForProduct(selectedProduct: IProduct, variations: Array<IProduct>) {
    if (!selectedProduct.hasVariations()) return;

    const marketplaceActive = variations?.find(variation => variation.marketplace);
    if (!marketplaceActive) return;

    selectedProduct.marketplace = marketplaceActive.marketplace;
    selectedProduct.marketplace_variation = marketplaceActive.id;
  }

  async function loadingCurrentProduct(id: any) {
    if (!id) return;

    try {
      updateState({ loading: true, loadingCoProducers: true, loadingDeliveryMethods: true });
      const cps = await productService.getCoproducers(id).toPromise();
      cps.active.forEach((item: ICoProducer) => {
        item.active = true;
      });
      cps.pending.forEach((item: ICoProducer) => {
        item.active = false;
        item.pending = true;
      });
      const cops = cps.active.concat(cps.pending);
      let { selectedProduct } = stateRef.current;
      selectedProduct.setCoProducers(cops);
      updateState({ selectedProduct, loadingCoProducers: false });
      const deliveryMethods = await productService.getDeliveryMethods(id);
      selectedProduct = stateRef.current.selectedProduct;
      selectedProduct.setDeliveries(deliveryMethods);

      if (selectedProduct.type === constants.PHYSICAL) {
        const delivery = selectedProduct.getPhysicalDelivery();
        setPhysicalDelivery(delivery);
      }

      updateState({ selectedProduct, loadingDeliveryMethods: false, loading: false });
      verifyValidations(selectedProduct);
    } catch (error) {
      toast.error(error);
      updateState({ loading: false });
    }
  }

  async function loadVariations(mainProduct: IProduct): Promise<Array<IProduct>> {
    const raw: Array<IProduct> = (await productService.getProductVariations(mainProduct.id)) as any;
    return raw.map((item: IProduct) => {
      const newItem = new ProductClass(item);
      newItem.setCategories(mainProduct.getIdCategories());
      return newItem;
    });
  }

  async function doLoadProduct(preloadedProduct?: IProduct, callback?: () => void) {
    const { productId, variationId } = routeParams;

    const loadedProduct: IProduct =
      preloadedProduct ?? new ProductClass((await productService.loadProductById(Number(productId), false)).data);

    const isVariationParam = !Object.values(eNameTabs).includes(variationId);
    updateState({ tabSelected: getDefaultTab(loadedProduct) });

    if (!productId) {
      history.goBack();
      toast.error('Produto não encontrado');
    }

    let mainProduct = new ProductClass(loadedProduct);
    let currentSelectedProduct: IProduct = mainProduct;
    currentSelectedProduct.friendly_name = stripEdzPrefix(currentSelectedProduct.friendly_name);

    if (!mainProduct.isMainProduct()) {
      const parentResult = await productService.loadProductById(mainProduct.parent_id, false);
      mainProduct = new ProductClass(parentResult.data);
    }

    updateState({ product: mainProduct });

    const localVariations = mainProduct.hasVariations() ? await loadVariations(mainProduct) : null;

    if (localVariations) {
      updateState({ variations: localVariations });
    }

    const selectedVariation =
      isVariationParam && localVariations ? localVariations.find(item => item.id === Number(variationId)) : null;

    if (selectedVariation?.id) {
      currentSelectedProduct = new ProductClass(selectedVariation);
      currentSelectedProduct.friendly_name = stripEdzPrefix(currentSelectedProduct.friendly_name);
      updateState({ variationId });
    }

    updateState({ selected: currentSelectedProduct.id, selectedProduct: currentSelectedProduct });
    handleMarketplaceForProduct(currentSelectedProduct, localVariations);
    updateState({ loadingGeneral: false });
    loadingCurrentProduct(currentSelectedProduct.id);

    if (!preloadedProduct) {
      queryClient.setQueryData(['product', routeProductId, false], loadedProduct);
    }

    callback?.();
    verifyValidations(currentSelectedProduct);
  }

  function sumComissionValues(coproducers: any[]): number {
    return coproducers.reduce((prev, current) => {
      const value =
        typeof current.comission_value === 'string'
          ? convertToNumber(current.comission_value)
          : current.comission_value;
      return prev + value;
    }, 0);
  }

  async function saveProduct(product: IProduct, variations: IProduct[]): Promise<number> {
    if (product.id !== 0) {
      await productService.updateProduct(product.id, product.serviceFormat());
      return product.id;
    }

    const createdProductData = await productService.createProduct(product.serviceFormat());
    const createdProduct = createdProductData.product ?? createdProductData;
    product.id = createdProduct.id;
    const vars: IProduct[] = variations.map(v => (v.id === 0 ? createdProduct : v));
    updateState({ variations: vars });
    return createdProduct.id;
  }

  async function handleSubmit(isDraft: boolean = false) {
    const { selectedProduct, variations } = stateRef.current;
    const newCoProducers = selectedProduct.getCoProducersForSubmit();
    const deliveriesWithoutNutrorHash: any[] = JSON.parse(
      JSON.stringify(selectedProduct.getDeliveries().filter(d => !d.nutror_hash))
    );
    const isProducerCommissionAutoManaged = (window as any).sumCoproducersComissions === 100;
    const producer = isProducerCommissionAutoManaged ? newCoProducers.find((c: any) => c.is_producer) : null;

    if (producer) {
      const nonProducerSum = sumComissionValues(newCoProducers.filter((c: any) => !c.is_producer));
      producer.comission_value = nonProducerSum > 100 ? 0 : Number((100 - nonProducerSum).toFixed(2));
    }

    const sumComissions = sumComissionValues(newCoProducers).toFixed(2);

    if (parseFloat(sumComissions) !== 100 && !selectedProduct.has_children) {
      toast.error('A soma dos percentuais de ganho devem ser igual a 100%.');
      return;
    }

    const productStatus = selectedProduct.getProductStatus();
    selectedProduct.active = isDraft
      ? EProductStatus.DRAFT
      : productStatus === EProductStatus.DRAFT
        ? EProductStatus.ACTIVE
        : productStatus;

    if (!selectedProduct.isMainWithChildren()) {
      const activeCoproducers = (newCoProducers ?? [])
        .filter((item: any) => item.active || (!item.pending && (item.new || item.edited)))
        .map((cp: any) => ({
          ...cp,
          comission_value: Number(functions.clearMoneyMask(cp.comission_value))
        }));
      selectedProduct.setCoProducers(activeCoproducers);
    }

    const newDeliveries = handleDelivery(selectedProduct, deliveriesWithoutNutrorHash);

    if (newDeliveries) {
      selectedProduct.setDeliveries(newDeliveries);
    }

    if (selectedProduct.type === constants.PHYSICAL) {
      selectedProduct.physicalDelivery = physicalDeliveryRef.current;
      stateRef.current.product.physicalDelivery = physicalDeliveryRef.current;
    }

    if (!isLegalPerson) {
      selectedProduct.allows_automatic_pix = false;
    }

    verifyValidations(selectedProduct);
    if (!isDraft && formHasError()) return;

    updateState({ saving: true });

    try {
      const id = await saveProduct(selectedProduct, variations);
      toast.success('Produto salvo com sucesso.');
      sendPostMessage({
        source: PostMessageSource.PRODUCT_MANAGER,
        action: PostMessageAction.PRODUCT_UPDATED,
        data: {
          product_id: id,
          is_draft: selectedProduct.active === EProductStatus.DRAFT,
          completed_at: new Date().toISOString(),
          product_type: selectedProduct.type,
          product_type_title: getProductTypeTitle(selectedProduct.type)
        }
      });
      (window as any).sumCoproducersComissions = null;
      const { tabSelected: currentTab } = stateRef.current;
      await doLoadProduct(undefined, () =>
        updateState({ saving: false, modifieds: {}, selected: id, tabSelected: currentTab })
      );
    } catch (error) {
      updateState({ saving: false });
      toast.error(error);
    }
  }

  function handleChange(name: string, value: any, callback?: (...args: any[]) => any) {
    const currentProduct = stateRef.current.selectedProduct;
    (currentProduct as any)[name] = value;

    if (!currentProduct.allows_card) {
      currentProduct.allows_multiple_cards = false;
    }

    const newProduct: IProduct = new ProductClass(currentProduct);
    const newModifieds = Object.assign({}, stateRef.current.modifieds);

    if (name !== 'recurring_frequency_type_select') {
      newModifieds[name] = true;
    }

    updateState({ selectedProduct: newProduct, modifieds: newModifieds });

    if (typeof callback === 'function') {
      callback();
    }
  }

  function updateSelectedProduct(product: Partial<IProduct>) {
    const selectedProduct = new ProductClass(product);
    updateState({ selectedProduct });
    verifyValidations(selectedProduct);
  }

  function copyFromOriginal() {
    let { selectedProduct, prevSelected } = stateRef.current;
    const prod: IProduct = {
      ...prevSelected.getGeneralProperties(),
      ...prevSelected.getSalesProperties(),
      ...prevSelected.getAffiliatesProperties()
    };
    selectedProduct = new ProductClass({ ...selectedProduct, ...prod });
    selectedProduct.setCoProducers(prevSelected.getCoProducers());
    selectedProduct.setDeliveries(prevSelected.getDeliveries());

    if (prevSelected.type === constants.PHYSICAL) {
      const delivery = prevSelected.getPhysicalDelivery();
      selectedProduct.setPhysicalDelivery(delivery);
      setPhysicalDelivery(delivery);
    }

    updateState({ selectedProduct, modifieds: { copied: true } });
    toast.success('Dados copiados com sucesso!');
    verifyValidations(selectedProduct);
  }

  function handleChangeTab(value: string) {
    updateState({ tabSelected: value });
  }

  function markModified(key: string): void {
    const { modifieds } = stateRef.current;
    updateState({ modifieds: { ...modifieds, [key]: 'edited' } });
  }

  const handleChangeCoproducer = useCallback((newCops: Array<ICoProducer>) => {
    const { selectedProduct } = stateRef.current;
    selectedProduct.setCoProducers(newCops);
    markModified('coproducers');
    updateState({ selectedProduct });
  }, []);

  function handleChangeDeliveries(deliveries: any) {
    const { selectedProduct } = stateRef.current;
    selectedProduct.setDeliveries(deliveries);
    markModified('deliveries');
    updateState({ selectedProduct });
  }

  function handleModifieds() {
    markModified('physical_delivery');
  }

  function changeItem(id: number) {
    let { product, variations, variationId } = stateRef.current;
    let selectedProduct: IProduct;

    if (product.id === id) {
      selectedProduct = new ProductClass(product);
      variationId = null;
    } else {
      selectedProduct = new ProductClass(variations.find((it: any) => it.id === id));
      variationId = id;
    }

    if (id === 0 && selectedProduct.type === constants.PHYSICAL) {
      resetPhysicalDelivery();
    }

    selectedProduct.friendly_name = stripEdzPrefix(selectedProduct.friendly_name);

    loadingCurrentProduct(id);
    updateState({
      selected: id,
      selectedProduct,
      confirmModal: false,
      modifieds: {},
      tabSelected: eNameTabs.General,
      variationId
    });
  }

  function onChangeItem(id: number) {
    const { modifieds, selectedProduct } = stateRef.current;
    const hasModified = Object.keys(modifieds).length;
    const isCreatingNewVariation = selectedProduct.isVariation() && selectedProduct.id === 0;

    if (hasModified || isCreatingNewVariation) {
      updateState({ confirmModal: true, nextItem: id });
      return;
    }

    changeItem(id);
  }

  async function onOkModal() {
    if (formHasError()) {
      updateState({ confirmModal: false });
      return;
    }

    try {
      updateState({ confirmModal: false });
      await handleSubmit();
    } catch (error) {}
  }

  function onCancelModal() {
    const { selectedProduct, cancelModalCallback } = stateRef.current;
    const isCreatingNewVariation = selectedProduct.isVariation() && !selectedProduct.id;

    if (selectedProduct.isVariation() || selectedProduct.hasVariations()) {
      changeItem(stateRef.current.nextItem);
    }

    if (isCreatingNewVariation) {
      const updatedVariations = stateRef.current.variations.filter(item => item.id !== 0);
      updateState({ variations: updatedVariations });
    }

    cancelModalCallback?.();
    updateState({ confirmModal: false });
  }

  function confirmCreateNewVariation() {
    const { product } = stateRef.current;

    if (Alumy.isAlumy(product.cnt_partner_cod)) {
      Alumy.alumyModal();
      return;
    }

    updateState({ variationModal: true });
  }

  function onBlinketModalClose() {
    updateState({ blinketModal: false });
  }

  function onBlinketModalOk() {
    const { variations, selectedProduct } = stateRef.current;
    const id = selectedProduct?.parent_id === null ? variations[0].id : selectedProduct?.id;
    if (!id) return;

    const redirectUrl = `${BLINKET_URL}/event/edit_by_cnt_cod/${id}`;
    window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    updateState({ blinketModal: false });
  }

  function onVariationModalOk() {
    updateState({ variationModal: false });
    newVariation();
  }

  function onVariationModalClose() {
    updateState({ variationModal: false });
  }

  const onChangeRequiredFields = useCallback(() => {
    const { selectedProduct } = stateRef.current;

    if (selectedProduct.type === constants.PHYSICAL) {
      selectedProduct.physicalDelivery = physicalDeliveryRef.current;
    }

    verifyValidations(selectedProduct);
  }, []);

  function onAddNewDeliveryPartner() {
    const { selectedProduct } = stateRef.current;
    const newCoProducers = selectedProduct.getCoProducers();
    const exist = newCoProducers.find((item: any) => item.email === 'kaiser@kapsula.com.br');

    if (exist) {
      toast.warn('Parceiro de entrega já faz parte dos parceiros deste produto');
      return;
    }

    const newCoproducer = new CoProducerClass({
      email: 'kaiser@kapsula.com.br',
      business_name: 'Kapsula Produtos Naturais Ltda',
      customer_id: 16640004,
      new: true,
      comission_type: 'P',
      isDeliveryPartner: true
    });
    newCoProducers.push(newCoproducer);
    handleChangeCoproducer(newCoProducers);
    updateState({ tabSelected: eNameTabs.Coproducers });
    toast.success('Parceiro de entrega adicionado com sucesso');
  }

  function onRemoveDeliveryPartner() {
    const { selectedProduct } = stateRef.current;
    const newCoProducers = selectedProduct.getCoProducers();
    const hasPartner = newCoProducers.some(item => item.email === 'kaiser@kapsula.com.br');
    if (!hasPartner) return;
    const nps = newCoProducers.filter((item: any) => item.email !== 'kaiser@kapsula.com.br');
    handleChangeCoproducer(nps);
    toast.success('Parceiro de entrega removido com sucesso');
  }

  function setupVariation(variation: IProduct, parentId: number, coproducer: any): void {
    variation.setParent(parentId);
    variation.setCoProducers([{ ...coproducer, comission_value: 100 }]);
    variation.setDeliveries(null);
    if (variation.type === constants.PHYSICAL) resetPhysicalDelivery();
  }

  function applyVariationToState(variation: IProduct, selectedProduct: IProduct, targetVariations: IProduct[]): void {
    targetVariations.push(variation);
    updateState({
      prevSelected: selectedProduct,
      variations: targetVariations,
      selectedProduct: variation,
      selected: variation.id,
      tabSelected: eNameTabs.General
    });
    verifyValidations(variation);
  }

  async function newVariation() {
    const { product, variations, selectedProduct } = stateRef.current;

    if (Alumy.isAlumy(product.cnt_partner_cod)) {
      Alumy.alumyModal();
      return;
    }

    const variation = new ProductClass({
      type: product.type,
      deliveries: [],
      charge_type: product.charge_type,
      allows_boleto: true,
      allows_card: true,
      allows_multiple_cards:
        product.charge_type !== EChargeType.SUBSCRIPTION && product.charge_type !== EChargeType.FREE,
      allows_pix: !isLegalPerson,
      allows_automatic_pix: isLegalPerson,
      author_name: product.author_name
    });

    const hasUnsavedVariation = product.hasVariations() && variations.some((item: IProduct) => item.id === 0);

    if (hasUnsavedVariation) {
      toast.info(
        'Existe uma nova variação que ainda não foi salva, por favor salve a variação antes de criar uma nova'
      );
      return;
    }

    if (product.hasVariations()) {
      setupVariation(variation, product.id, selectedProduct.coproducers[0]);
      applyVariationToState(variation, selectedProduct, variations);
      return;
    }

    updateState({ saving: true });
    await productService.cloneProduct(product.id, product.id);
    updateState({ saving: false });
    await doLoadProduct(undefined, () => {
      const { variations: currentVariations } = stateRef.current;
      setupVariation(variation, product.id, selectedProduct.coproducers[0]);
      applyVariationToState(variation, selectedProduct, currentVariations);
    });
  }

  async function handleClickPriceSubmit() {
    const confirm = await AlertComponent.show({
      message:
        'Você está alterando o valor de novas assinaturas, onde contratos antigos não serão impactados. Deseja reajustar os antigos também?',
      title: 'Aviso sobre contratos antigos',
      buttonOk: 'Ir para reajustes',
      buttonCancel: 'Manter para novos contratos',
      confirmation: true
    });

    if (confirm) {
      await handleSubmit();
      window.location.href = `${VERTICAL_URL}/subscriptions/readjustment`;
      return;
    }

    handleSubmit();
  }

  async function handleClickSubmitDraft() {
    const confirm = await AlertComponent.show({
      title: 'Quase lá...',
      caption: 'Seu produto será salvo como rascunho. Para publicar, volte a editar esse produto.',
      message: 'Salve seu produto e continue editando depois, ainda não será gerado um link de vendas do checkout.',
      confirmation: true,
      buttonOk: 'Confirmar e salvar',
      size: 'md'
    });

    if (confirm) {
      handleSubmit(true);
    }
  }

  async function handleClickSubmit() {
    const message = (
      <>
        Concordo que meu produto está em conformidade com as recomendações para evitar interrupções nas vendas durante o
        processo de análise e moderação.
        <Alert
          type='info'
          className='mt-2'
          message={
            <>
              Caso seja reprovado na moderação, seu produto ficará indisponível para venda.{' '}
              <a
                href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807655450-Como-funciona-a-modera%C3%A7%C3%A3o-de-conte%C3%BAdo'
                rel='noopener noreferrer'
                target='_blank'
              >
                Saiba mais
              </a>
            </>
          }
        />
      </>
    );
    const confirm = await AlertComponent.show({
      title: 'Quase lá...',
      caption: 'Talvez seu produto seja enviado para a moderação, mas não se preocupe, você pode continuar vendendo.',
      message,
      confirmation: true,
      buttonOk: 'Confirmar e cadastrar',
      size: 'md'
    });

    if (confirm) {
      handleSubmit();
    }
  }

  function openConfirmModal(cancelModalCallback?: undefined | (() => void)) {
    updateState({ confirmModal: true, cancelModalCallback });
  }

  useEffect(() => {
    if (!fetchedProduct) return;
    doLoadProduct(fetchedProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedProduct?.id]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const { productId } = routeParams;
    const { tabSelected, variationId } = stateRef.current;

    if (variationId) {
      history.replace(`/producer/product/editproduct/${productId}/${variationId}/${tabSelected}`);
      return;
    }

    history.replace(`/producer/product/editproduct/${productId}/${tabSelected}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tabSelected, state.variationId]);

  const {
    loadingGeneral,
    loadingCoProducers,
    loadingDeliveryMethods,
    editing,
    saving,
    loading,
    generalRequiredFields,
    advancedRequiredFields,
    salesRequiredFields,
    coProducerRequiredFields,
    affiliatesRequiredFields,
    InfoDeliveryRequiredFields,
    PhysicalDeliveryRequiredFields,
    paymentMethodsRequiredFields,
    recoveryRequiredFields,
    selectedProduct,
    selected,
    product,
    tabSelected,
    modifieds,
    variations
  } = state;

  const hasVariations = product.hasVariations();
  const hasModifiedRecurringValue = 'recurring_value' in modifieds;
  const isDirty = Object.keys(modifieds).length;
  const isValid = (() => {
    return [
      loadingGeneral,
      loadingCoProducers,
      loadingDeliveryMethods,
      editing,
      saving,
      loading,
      generalRequiredFields,
      advancedRequiredFields,
      salesRequiredFields,
      coProducerRequiredFields,
      affiliatesRequiredFields,
      InfoDeliveryRequiredFields,
      PhysicalDeliveryRequiredFields,
      paymentMethodsRequiredFields,
      recoveryRequiredFields
    ].reduce((accumulator: any, currentValue: any) => accumulator + currentValue);
  })();

  const isMainProduct = selectedProduct?.isMainWithChildren();
  const isInfo = selectedProduct?.type === constants.INFO;
  const isPhysical = selectedProduct?.type === constants.PHYSICAL;
  const isService = selectedProduct?.type === constants.SERVICE;
  const isTicket = selectedProduct?.type === constants.TICKET;
  const infoDeliveryLabel = isInfo ? 'Entrega' : 'Entrega online';
  const isPhysicalNoFather = !isMainProduct && isPhysical;
  const isLoading = loading || saving;
  const currentProductTitleWithId = `${selectedProduct?.id || product?.id || ''} - ${selectedProduct?.title || product?.title || ''}`;
  const isDraft = selectedProduct.active === EProductStatus.DRAFT;
  const isMainSelected = isMainProduct && selectedProduct?.id === product?.id;
  const isFree = selectedProduct.charge_type === EChargeType.FREE;
  const isOpenValue = selectedProduct.charge_type === EChargeType.OTHER;
  const disableCreateVariationButton = isLoading || isDraft || isService || isTicket || isMainSelected;
  const variationTooltipButton = isDraft
    ? 'Não é possível criar variação de um produto que está como rascunho'
    : isMainSelected
      ? 'Crie variação somente com uma variação selecionada'
      : isTicket
        ? 'Esta ação só pode ser feita na Blinket'
        : '';
  const isBetaReadjustment = eduzzAccountsService.verifyUserHasBetaModule('assinaturas-reajuste');
  const isUnity = user?.is_clube_black;
  const isElite = user?.is_elite;
  const isSinglePayment = product.charge_type === EChargeType.SINGLE;
  const hasOffersAccess = isSinglePayment && isInfo;
  const isSubscriptionPlan = product.charge_type === EChargeType.SUBSCRIPTION;
  const userHasAccessToSubscriptionModule = eduzzAccountsService.verifyUserHasBetaModule('planos_assinatura');
  const enableSubscriptionTab = isSubscriptionPlan && userHasAccessToSubscriptionModule;
  const allowTMBPayment = selectedProduct?.beta.includes('product_payment_tmb');

  const myTabs = [
    { name: eNameTabs.General, label: 'Geral', errors: generalRequiredFields, show: true },
    {
      name: eNameTabs.Sales,
      label: hasOffersAccess ? 'Preços e ofertas' : 'Vendas',
      errors: salesRequiredFields,
      show: !isMainProduct && !enableSubscriptionTab
    },
    {
      name: eNameTabs.SubscriptionPlan,
      label: 'Planos de assinatura',
      errors: salesRequiredFields,
      show: !isMainProduct && enableSubscriptionTab
    },
    {
      name: eNameTabs.PaymentMethods,
      label: 'Meios de pagamento',
      errors: paymentMethodsRequiredFields,
      show: !isFree && !isMainProduct
    },
    { name: eNameTabs.Coproducers, label: 'Parceiros', errors: coProducerRequiredFields, show: !isMainProduct },
    { name: eNameTabs.Affiliates, label: 'Afiliados', errors: affiliatesRequiredFields, show: true },
    {
      name: eNameTabs.InfoDelivery,
      label: infoDeliveryLabel,
      errors: InfoDeliveryRequiredFields,
      show: !isMainProduct
    },
    {
      name: eNameTabs.PhysicalDelivery,
      label: 'Entrega física',
      errors: PhysicalDeliveryRequiredFields,
      show: isPhysicalNoFather
    },
    {
      name: eNameTabs.SalesRecovery,
      label: 'Recuperação de vendas',
      errors: recoveryRequiredFields,
      show: !isMainProduct && !isOpenValue
    },
    { name: eNameTabs.Advanced, label: 'Avançado', errors: advancedRequiredFields, show: !isMainProduct }
  ];

  const coproducers = selectedProduct.getCoProducers();
  const hasElevarAdded = coproducers.some(coproducer => PROJECT_ELEVAR_ID.includes(coproducer.customer_id));
  const coProducersLimit = hasElevarAdded ? coProducersLimitWithElevar : coProducersLimitWithoutElevar;
  const canAddCoproducer = coproducers.length < coProducersLimit;
  const coProducerErrorMessage = canAddCoproducer
    ? ''
    : 'Não é possível adicionar mais de 15 coprodutores para o mesmo produto';
  const showReadjustmentAlert = hasModifiedRecurringValue && (isBetaReadjustment || isUnity || isElite);
  const canSaveDraft = !!selectedProduct?.title;

  const tabItems = myTabs
    .filter(t => t.show)
    .map(tab => {
      const isAffiliatesTab = tab.name === eNameTabs.Affiliates;
      const isChargeTypeFree = selectedProduct.charge_type === EChargeType.FREE;
      const hasChildren = selectedProduct.has_children;
      return {
        key: tab.name,
        label: (
          <Tooltip
            placement='top'
            title={
              isAffiliatesTab && isChargeTypeFree && !hasChildren
                ? 'O recurso de afiliado está indisponível para produtos gratuitos'
                : ''
            }
          >
            <span>
              <LabelTab label={tab.label} errors={tab.errors} />
            </span>
          </Tooltip>
        ),
        disabled: !hasChildren && isAffiliatesTab && isChargeTypeFree
      };
    });

  return (
    <>
      <div className='mt-4'>
        <div className='mb-4'>
          <Breadcrumb
            items={[
              { title: <Link to='/producer/product/myproducts'>Meus produtos</Link> },
              { title: isService ? 'Edição do serviço' : 'Edição do produto' }
            ]}
          />
        </div>
        {allowTMBPayment && (
          <Alert
            style={{ margin: '16px 0' }}
            type='warning'
            message={
              <>
                <strong>Atenção!</strong> Se este produto possui o parcelamento via TMB ativo, alterações no produto
                podem afetar o valor exibido no Checkout da TMB ou até remover essa forma de pagamento no Checkout da
                Eduzz. <br />
                Para garantir o funcionamento correto, comunique seu consultor técnico responsável sempre que fizer
                qualquer alteração neste produto.
              </>
            }
          />
        )}

        <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {isDraft && <DraftLabel />}
            <span className='text-xl font-semibold'>
              <Truncate count={hasVariations ? 70 : 60} final='...'>
                {currentProductTitleWithId}
              </Truncate>
            </span>
          </div>
          <div>
            {!isTicket && (
              <Tooltip title={variationTooltipButton}>
                <div>
                  <Button
                    icon={isLoading ? <Spin size='small' /> : <DynamicFeedOutlined />}
                    disabled={disableCreateVariationButton}
                    onClick={hasVariations ? newVariation : confirmCreateNewVariation}
                    type='primary'
                  >
                    Nova variação
                  </Button>
                </div>
              </Tooltip>
            )}
            {isTicket && (
              <Button
                icon={isLoading ? <Spin size='small' /> : <EditOutlined />}
                onClick={() => updateState({ blinketModal: true })}
                type='primary'
              >
                Editar evento na Blinket
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className='flex gap-4 max-md:flex-col'>
        {hasVariations && (
          <div className='w-1/4 overflow-y-auto max-md:w-full' style={{ top: TOOLBAR_HEIGHT }}>
            <div className='rounded-sm border border-[#e0e0e0]'>
              <div
                className={`flex cursor-pointer items-center gap-3 border-b border-[#e0e0e0] p-3 ${selected === product.id ? 'bg-blue-50' : ''}`}
                onClick={() => onChangeItem(product.id)}
              >
                <Avatar icon={<SettingsIcon />} />
                <div>
                  <h4 className='mb-0!'>Configurações Gerais</h4>
                  <b>{product.id}</b>
                  <div className='text-sm text-gray-500'>{product.title}</div>
                </div>
              </div>
              <div className='border-b border-[#e0e0e0] p-2'>
                <h5 className='mb-0! text-sm!'>
                  Esse produto contém {variations?.length} {variations?.length > 1 ? 'variações' : 'variação'}
                </h5>
              </div>
              {hasVariations && !variations && (
                <div className='flex items-center justify-center p-4'>
                  <Spin />
                </div>
              )}
              <div className='max-h-[60vh] overflow-y-auto'>
                {(variations || []).map(item => (
                  <div
                    key={item.id}
                    className={`flex cursor-pointer items-center gap-3 border-b border-[#e0e0e0] p-3 ${selected === item.id ? 'bg-blue-50' : ''} ${
                      isLoading ? 'pointer-events-none opacity-50' : ''
                    }`}
                    onClick={selected !== item.id ? () => onChangeItem(item.id) : undefined}
                  >
                    <Avatar src={item.image} icon={<AddIcon />} alt={`Imagem da Variação ${item.id}`} />
                    <div>
                      {item?.id ? (
                        <p className='mb-0!'>
                          <strong>{item.id}</strong>
                        </p>
                      ) : (
                        'Nova variação'
                      )}
                      <div className='text-sm text-gray-500'>{item.title}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={`${hasVariations ? 'w-3/4 max-md:w-full' : 'w-full'} relative`}>
          <Tabs
            activeKey={tabSelected}
            onChange={handleChangeTab}
            items={tabItems}
            tabBarExtraContent={
              !isLoading && selectedProduct?.id === 0 ? (
                <Button type='text' onClick={copyFromOriginal}>
                  Copiar do produto
                </Button>
              ) : null
            }
          />
          <div style={{ marginTop: 12 }}>
            {tabSelected === eNameTabs.General && (
              <General
                loading={isLoading}
                fade={tabSelected === eNameTabs.General}
                handleChange={handleChange}
                product={selectedProduct}
                modifieds={modifieds}
                handleImageChange={handleChange}
                editing={editing}
                categoryList={categories}
                onChangeRequiredFields={onChangeRequiredFields}
                variations={variations}
              />
            )}
            {tabSelected === eNameTabs.Sales && (
              <Sales
                loading={isLoading}
                fade={tabSelected === eNameTabs.Sales}
                handleChange={handleChange}
                product={selectedProduct}
                modifieds={modifieds}
                editing={editing}
                onChangeRequiredFields={onChangeRequiredFields}
                openConfirmModal={openConfirmModal}
              />
            )}
            {tabSelected === eNameTabs.PaymentMethods && (
              <PaymentMethods
                loading={isLoading}
                fade={tabSelected === eNameTabs.PaymentMethods}
                handleChange={handleChange}
                product={selectedProduct}
                modifieds={modifieds}
                editing={editing}
                onChangeRequiredFields={onChangeRequiredFields}
              />
            )}
            {tabSelected === eNameTabs.Coproducers && (
              <CoProducer
                loading={loadingCoProducers || saving}
                fade={tabSelected === eNameTabs.Coproducers}
                coproducers={selectedProduct.coproducers}
                canAddCoproducer={canAddCoproducer}
                coProducerErrorMessage={coProducerErrorMessage}
                hasElevarAdded={hasElevarAdded}
                onChange={handleChangeCoproducer}
                onChangeRequiredFields={onChangeRequiredFields}
              />
            )}
            {tabSelected === eNameTabs.Affiliates && (
              <Affiliate
                loading={loadingCoProducers || saving}
                fade={tabSelected === eNameTabs.Affiliates}
                handleChange={handleChange}
                product={selectedProduct}
                modifieds={modifieds}
                editing={editing}
                onChangeRequiredFields={onChangeRequiredFields}
                userHasSubscriptionModule={userHasAccessToSubscriptionModule}
                updateProduct={updateSelectedProduct}
              />
            )}
            {tabSelected === eNameTabs.InfoDelivery && (
              <InfoDelivery
                loading={loadingDeliveryMethods || saving}
                fade={tabSelected === eNameTabs.InfoDelivery}
                deliveries={selectedProduct.deliveries}
                onChange={handleChangeDeliveries}
                onChangeRequiredFields={onChangeRequiredFields}
                isInfo={product.type == constants.INFO}
                offer={selectedProduct}
                isService={product.type == constants.SERVICE}
                enabledValidation
              />
            )}
            {selectedProduct.type === constants.PHYSICAL && tabSelected === eNameTabs.PhysicalDelivery && (
              <PhysicalDelivery
                loading={loadingDeliveryMethods || saving}
                fade={tabSelected === eNameTabs.PhysicalDelivery}
                onEdit={handleModifieds}
                onChangeRequiredFields={onChangeRequiredFields}
                onAddPartner={onAddNewDeliveryPartner}
                onRemovePartner={onRemoveDeliveryPartner}
                product={product}
                enabledValidation
              />
            )}
            {tabSelected === eNameTabs.SalesRecovery && (
              <SalesRecovery
                loading={isLoading}
                fade={tabSelected === eNameTabs.SalesRecovery}
                handleChange={handleChange}
                product={selectedProduct}
                modifieds={modifieds}
                editing={editing}
                onChangeRequiredFields={onChangeRequiredFields}
              />
            )}
            {tabSelected === eNameTabs.Advanced && (
              <AdvancedForm
                loading={isLoading}
                product={selectedProduct}
                fade={tabSelected === eNameTabs.Advanced}
                onChangeRequiredFields={onChangeRequiredFields}
                handleChange={handleChange}
              />
            )}

            <div className='mt-4 flex justify-end gap-2'>
              {selectedProduct.active === EProductStatus.DRAFT ? (
                <DraftButtons
                  disabled={{ tooltip: !isValid, button: isValid || loading || saving }}
                  disabledDraft={{ tooltip: !!canSaveDraft, button: !canSaveDraft }}
                  onSubmit={() => handleClickSubmit()}
                  onSubmitDraft={() => handleClickSubmitDraft()}
                  isService={isService}
                />
              ) : (
                <div className='flex justify-end'>
                  <Tooltip
                    placement='top'
                    title={isValid ? 'Por favor verifique os campos inválidos antes de salvar' : ''}
                  >
                    <div>
                      <Button
                        type='primary'
                        disabled={!isDirty || isValid || loading || saving}
                        onClick={showReadjustmentAlert ? handleClickPriceSubmit : () => handleSubmit()}
                        icon={<Save />}
                      >
                        {saving ? 'Salvando...' : 'Salvar'}
                      </Button>
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ModalActions
        title='Atenção'
        open={state.confirmModal}
        buttonOk='Salvar'
        buttonOkColor='#009358'
        buttonCancel='Não salvar'
        onOk={onOkModal}
        onClose={onCancelModal}
      >
        As alterações realizadas não foram salvas e serão perdidas, deseja salvar?
      </ModalActions>
      <ModalActions
        title='Deseja realmente adicionar variações para este produto?'
        open={state.variationModal}
        onClose={onVariationModalClose}
        onOk={onVariationModalOk}
        buttonOk='Sim, adicionar variação'
        buttonCancel='Não'
      >
        <p>
          Após a criação de uma variação o conteúdo deverá ter no <b>mínimo 2 variações</b>, esta alteração não poderá
          ser desfeita.
        </p>
        {product.connects > 0 && (
          <p style={{ marginTop: 10, fontWeight: 500 }}>
            Você possui {product.connects} {product.connects > 1 ? 'ações' : 'ação'} do connect{' '}
            {product.connects > 1 ? 'cadastradas' : 'cadastrada'} nesse produto, ajuste{' '}
            {product.connects > 1 ? 'as ações' : 'a ação'} do seu connect para essa variação.
          </p>
        )}
      </ModalActions>
      <ModalActions
        title='Para editar é necessário acessar a Blinket'
        open={state.blinketModal}
        onClose={onBlinketModalClose}
        onOk={onBlinketModalOk}
        buttonOk='Continuar para a Blinket'
        buttonCancel='Cancelar'
      >
        <p>
          Informações sobre seu evento são editados na Blinket, enquanto configurações de entregas ou de conversões são
          no MyEduzz. Você será direcionado para a Blinket.
        </p>
      </ModalActions>
    </>
  );
};

const LabelTab = ({ label, errors }: { label: string | React.ReactNode; errors: number }) => {
  if (!errors) return <>{label}</>;
  return (
    <Badge count={errors} size='small'>
      {label}
    </Badge>
  );
};

export default Product;
